import { formatDateCompact, formatDateToIso } from './date';
import { urlEnum } from './url';
import { GuideCard } from 'features/guides/components/GuideCard/GuideCard.component';
import { GuideEpisodesHeader } from 'features/guides/components/GuideEpisodesHeader/GuideEpisodesHeader.component';
import { InnovationCard } from 'features/innovations/components/InnovationCard/InnovationCard.component';
import { InnovationHighlightCard } from 'features/innovations/components/InnovationHighlightCard/InnovationHighlightCard.component';
import { ServiceCard } from 'features/services/components/ServiceCard/ServiceCard.component';
import compact from 'lodash/compact';
import {
  ActivityCode,
  Article,
  Content,
  ContentType,
  Guide,
  InterestCode,
  Service,
  Startup,
  Tag,
} from 'pleinchamp-api-client';
import React, { ComponentProps } from 'react';
import { ActuCard } from '@actus/components/ActuCard/ActuCard.component';
import { ClippedImage } from '@components/ClippedImage/ClippedImage.component';
import { ContentCardLight } from '@components/content/ContentCardLight/ContentCardLight.component';
import { PlcImgLoader } from '@components/Loaders/PlcImgLoader.component';
import { PlcImg } from '@components/PlcImg/PlcImg.component';
import { Text } from '@components/Text/Text.component';
import { TextWithArrow } from '@components/Text/TextWithArrow.component';
import { EventCard } from '@events/components/EventCard/EventCard.component';
import { PlcColor } from '@styles/colors';
import { usePlcRouter } from '@utils/customHooks';
import { ComponentVersion } from 'features/business/app.types';
import { ServiceContentType } from './api.utils';
import { ClippedImageStyle } from './images';

type Tags = (ActivityCode | InterestCode)[];

function getThumbnail(content: Article | Guide | Content | Startup, isMobile?: boolean) {
  switch (true) {
    case content.contentType === ContentType.Startup:
      return (content as Startup).logo;
    case Object.prototype.hasOwnProperty.call(content, 'articleDetail'):
      return isMobile && (content as Content).articleDetail?.thumbnailMediaMobile
        ? (content as Content).articleDetail?.thumbnailMediaMobile
        : (content as Content).articleDetail?.thumbnailMedia;
    case Object.prototype.hasOwnProperty.call(content, 'guideDetail'):
      return isMobile && (content as Content).guideDetail?.thumbnailMediaMobile
        ? (content as Content).guideDetail?.thumbnailMediaMobile
        : (content as Content).guideDetail?.thumbnailMedia;
    case Object.prototype.hasOwnProperty.call(content, 'serviceDetail'):
      return isMobile && (content as Content).serviceDetail?.thumbnailMediaMobile
        ? (content as Content).serviceDetail?.thumbnailMediaMobile
        : (content as Content).serviceDetail?.thumbnailMedia;
    default:
      return isMobile && (content as Article | Guide).thumbnailMediaMobile
        ? (content as Article | Guide).thumbnailMediaMobile
        : (content as Article | Guide).thumbnailMedia;
  }
}

function getThumbnailImage(
  content: Article | Guide | Content | Startup,
  loaderProps?: Omit<ComponentProps<typeof PlcImgLoader>, 'uniqueKey'>,
  height?: number,
  width?: number,
  isMobile?: boolean
) {
  const thumbnailMedia = getThumbnail(content, isMobile);

  return thumbnailMedia ? (
    <PlcImg
      alt=""
      loader={
        <PlcImgLoader
          height={height}
          uniqueKey={`thumbnailMedia-${thumbnailMedia.id}`}
          width={width}
          {...loaderProps}
        />
      }
      plcSrc={{ src: thumbnailMedia.url, type: 'external' }}
    />
  ) : null;
}

function buildActuCard(
  content: Article | Content,
  tags: Tags,
  isMobile?: boolean,
  componentVersion?: ComponentVersion,
  index?: number,
  newsHeadlines?: Content[],
  imgWidth?: number,
  imgHeight?: number
) {
  if (content.contentType !== ContentType.Article) {
    return null;
  }

  const { title, publishDate, isPro, slug } = content;
  const route = usePlcRouter();

  if (route.asPath === urlEnum.home) {
    return (
      <ActuCard
        cardImage={getThumbnailImage(content, undefined, imgHeight, imgWidth, isMobile)}
        cardTitle={title}
        componentVersion={componentVersion}
        index={index}
        isPro={isPro}
        key={slug}
        newsHeadlines={newsHeadlines}
        slug={slug}
      />
    );
  }

  return (
    <ActuCard
      cardImage={getThumbnailImage(content, undefined, imgHeight, imgWidth, isMobile)}
      cardTitle={title}
      componentVersion={componentVersion}
      date={publishDate}
      index={index}
      isPro={isPro}
      key={slug}
      newsHeadlines={newsHeadlines}
      slug={slug}
      tags={tags}
    />
  );
}

function buildEventCard(content: Content, componentVersion?: ComponentVersion) {
  if (content.contentType !== ContentType.Event || !content.eventDetail) {
    return null;
  }

  const { eventDetail, slug, title } = content;
  const { startDate, endDate, address } = eventDetail;

  return (
    <EventCard
      address={address}
      cardTitle={title}
      componentVersion={componentVersion}
      endDate={endDate}
      key={slug}
      slug={slug}
      startDate={startDate}
    />
  );
}

function buildGuideCard(
  content: Guide | Content,
  tags: Tags,
  componentVersion?: ComponentVersion,
  imgWidth?: number,
  imgHeight?: number
) {
  if (content.contentType !== ContentType.Guide) {
    return null;
  }

  const { title, isPro } = content;
  const episodesList = Object.prototype.hasOwnProperty.call(content, 'guideDetail')
    ? (content as Content).guideDetail!.episodeList
    : (content as Guide).episodeList;
  const slug = episodesList[0];

  return (
    <GuideCard
      cardImage={getThumbnailImage(content, undefined, imgHeight, imgWidth)}
      cardTitle={title}
      componentVersion={componentVersion}
      episodesCount={episodesList.length - 1}
      guide={content}
      isPro={isPro}
      key={slug}
      slug={slug}
      tags={tags}
      title={title}
    />
  );
}

function isService(content: Service | Content): content is Service {
  return !Object.prototype.hasOwnProperty.call(content, 'serviceDetail');
}

function buildServiceCard(
  content: Service | Content,
  isFavoris: boolean | undefined,
  componentVersion?: ComponentVersion,
  imgWidth?: number,
  imgHeight?: number
) {
  const serviceContentTypes = [ContentType.PlcService, ContentType.AffiliateService];
  if (!serviceContentTypes.includes(content.contentType)) {
    return null;
  }

  const { id, title, slug, isPro, contentType } = content;
  const { affiliateServiceDetail, plcServiceDetail, subtitle } = isService(content) ? content : content.serviceDetail!;

  return slug ? (
    <ServiceCard
      cardImage={getThumbnailImage(content, { fill: PlcColor.WHITE }, imgHeight, imgWidth)}
      cardTitle={subtitle}
      componentVersion={componentVersion}
      id={id}
      isFavoris={isFavoris}
      isPro={isPro}
      key={slug}
      rating={affiliateServiceDetail?.averageRating}
      serviceUrl={plcServiceDetail?.serviceURL}
      slug={slug}
      title={title}
      type={contentType as ServiceContentType}
    />
  ) : null;
}

export function buildTagList(activityCodes: Content['activityCodes'], interestCodes: Content['interestCodes']) {
  let tags: (ActivityCode | InterestCode | undefined)[] = [];
  tags = tags.concat(activityCodes, interestCodes);
  return compact(tags);
}

type CardContent = Article | Guide | Service | Content;

export const buildContentCard = (
  content: CardContent,
  isMobile?: boolean,
  componentVersion?: ComponentVersion,
  index?: number,
  newsHeadlines?: Content[],
  imgWidth?: number,
  imgHeight?: number
) => {
  switch (content.contentType) {
    case ContentType.Article: {
      const tags = buildTagList(content.activityCodes, content.interestCodes);
      return buildActuCard(content, tags, isMobile, componentVersion, index, newsHeadlines, imgWidth, imgHeight);
    }
    case ContentType.Event: {
      return buildEventCard(content, componentVersion);
    }
    case ContentType.Guide: {
      const tags = buildTagList(content.activityCodes, content.interestCodes);
      return buildGuideCard(content, tags, componentVersion, imgWidth, imgHeight);
    }
    case ContentType.PlcService:
    case ContentType.AffiliateService: {
      return buildServiceCard(content, false, componentVersion, imgWidth, imgHeight);
    }
    default: {
      return null;
    }
  }
};
export const buildContentFavorisServiceCard = (content: CardContent) => {
  switch (content.contentType) {
    case ContentType.Article: {
      const tags = buildTagList(content.activityCodes, content.interestCodes);
      return buildActuCard(content, tags);
    }
    case ContentType.Event: {
      return buildEventCard(content);
    }
    case ContentType.Guide: {
      const tags = buildTagList(content.activityCodes, content.interestCodes);
      return buildGuideCard(content, tags);
    }
    case ContentType.PlcService:
    case ContentType.AffiliateService: {
      return buildServiceCard(content, true);
    }
    default: {
      return null;
    }
  }
};

export function generateLightCardFromArticle(content: Content, index: number) {
  if (content.contentType !== ContentType.Article) {
    return <></>;
  }

  const { publishDate, isPro, slug, title } = content;
  const header = (
    <Text dateTime={formatDateToIso(publishDate)} tag="time" variant="h6">
      {formatDateCompact(publishDate)}
    </Text>
  );

  return (
    <ContentCardLight
      header={header}
      href={urlEnum.actu}
      index={index}
      isPro={isPro}
      isWithBorder={true}
      isWithChevron={true}
      key={slug}
      slug={slug}
      title={title}
    />
  );
}

export function generateLightCardFromGuide(content: Content) {
  if (content.contentType !== ContentType.Guide || !content.guideDetail) {
    return <></>;
  }
  const { isPro, title, guideDetail } = content;
  const { episodeList } = guideDetail;
  const episodesCount = episodeList.length - 1;
  const slug = episodeList[0];
  const header = <GuideEpisodesHeader count={episodesCount} />;

  return <ContentCardLight header={header} href={urlEnum.guides} isPro={isPro} key={slug} slug={slug} title={title} />;
}

/**
 * Build the startup card
 * @param content
 * @param domainsList as Tag[]
 * @param stakesList as Tag[]
 * @param sectorsList as Tag[]
 * @param isHighlightCard
 * @returns Startup component
 */
export function buildStartupCard(
  content: Startup,
  domainsList: Tag[],
  stakesList: Tag[],
  sectorsList: Tag[],
  isHighlightCard?: boolean
) {
  const { name, description, publishDate, slug, stakeCodes, domainCodes, sectorCodes } = content;

  const domains =
    domainsList && domainCodes
      ? domainsList.filter((domain) =>
          domainCodes.some(
            (domainCode) =>
              domainCode === domain.remoteId ||
              (domain.children ? domain.children.map((e) => e.remoteId).includes(domainCode) : false)
          )
        )
      : ([] as Tag[]);
  const sectors =
    sectorsList && sectorCodes
      ? sectorsList.filter((sector) => sectorCodes.some((sectorCode) => sectorCode === sector.remoteId))
      : ([] as Tag[]);
  const stakes =
    stakesList && stakeCodes
      ? stakesList.filter((stake) => stakeCodes.some((stakeCode) => stakeCode === stake.remoteId))
      : ([] as Tag[]);

  return (
    <InnovationCard
      cardImage={getThumbnailImage(content, {}, 150)}
      date={publishDate}
      description={description}
      domains={domains}
      isHighlightCard={isHighlightCard}
      key={slug}
      sectors={sectors}
      slug={slug}
      stakes={stakes}
      title={name}
    />
  );
}

/**
 * Build the highlight startup card
 * @param content HighLightStartupCard Component
 * @param domainsList as Tag[]
 * @param stakesList as Tag[]
 * @param sectorsList as Tag[]
 */
export function buildHighlightStartupCard(content: Startup, domainsList: Tag[], stakesList: Tag[], sectorsList: Tag[]) {
  const { name, description, publishDate, slug, stakeCodes, domainCodes, sectorCodes } = content;

  const domains = domainsList
    ? domainsList.filter((domain) =>
        domainCodes.some(
          (domainCode) =>
            domainCode === domain.remoteId ||
            (domain.children ? domain.children.map((e) => e.remoteId).includes(domainCode) : false)
        )
      )
    : ([] as Tag[]);
  const sectors = sectorsList
    ? sectorsList.filter((sector) => sectorCodes.some((sectorCode) => sectorCode === sector.remoteId))
    : ([] as Tag[]);
  const stakes = stakesList
    ? stakesList.filter((stake) => stakeCodes.some((stakeCode) => stakeCode === stake.remoteId))
    : ([] as Tag[]);

  const thumbnailMedia = content.image;
  const clippedImage = (
    <ClippedImage
      alt={thumbnailMedia?.alt || ''}
      clipStyle={ClippedImageStyle.innovationHighlightcard}
      plcSrc={{ src: thumbnailMedia?.url || '', type: 'external' }}
    />
  );
  const actions = <TextWithArrow i18nKey="innovations.card-action" tag="span" variant="p" />;

  return (
    <InnovationHighlightCard
      cardActions={actions}
      cardImage={clippedImage}
      date={publishDate}
      description={description}
      domains={domains}
      sectors={sectors}
      slug={slug}
      stakes={stakes}
      startUpImage={getThumbnailImage(content, {}, 100)}
      title={name}
    />
  );
}
